import { useCallback, useEffect, useRef, useState } from 'react';
import { IconArrowsHorizontal, IconMinus, IconPlus, IconMaximize } from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const STEP = 0.25;

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

/**
 * Paints the processed preview ImageData into a contained canvas, with
 * zoom + pan over it.
 *
 * Zoom is a CSS transform on the canvas rather than a bigger canvas: the bitmap
 * is already the full working image, so scaling it costs nothing and a slider
 * drag never has to re-render at zoom level. Zoom 1 = fit; there is no zoom-out
 * past fit because there is nothing to see there.
 *
 * The view lives in REFS, with state only mirroring it for render. Every gesture
 * here reads the current view to compute the next one (pan needs the live offset,
 * anchored zoom needs the live scale), and a handler that read them from render
 * scope would act on a stale view whenever two inputs land in the same frame —
 * that is how a pan begun right after a zoom change strands the page off-screen.
 *
 * Page actions — crop, rotate, compare, print — ride on a hover bar over the
 * image, the same shape the PDF page grid uses. Below `md` the bar stays visible:
 * there is no hover on a touch screen, and an invisible toolbar is no toolbar.
 */
export default function DocumentCleanupPreview({
    imageData,
    /** The untouched page, shown on the far side of the compare slider. */
    compareImageData = null,
    compare = false,
    labels = {},
    actions = [],
}) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const compareCanvasRef = useRef(null);
    const frameRef = useRef(null);
    const viewportRef = useRef(null);
    const pointersRef = useRef(new Map());
    const panRef = useRef(null);
    const pinchRef = useRef(null);

    const zoomRef = useRef(1);
    const offsetRef = useRef({ x: 0, y: 0 });
    const [view, setView] = useState({ zoom: 1, offset: { x: 0, y: 0 } });

    /** The one way the view changes. Refs first, render second. */
    const applyView = useCallback((zoomValue, offsetValue) => {
        const zoom = clampZoom(zoomValue);
        // Fitted means centred — there is nothing to pan to at 1×.
        const offset = zoom === 1 ? { x: 0, y: 0 } : offsetValue;
        zoomRef.current = zoom;
        offsetRef.current = offset;
        setView({ zoom, offset });
    }, []);

    const resetView = useCallback(() => applyView(1, { x: 0, y: 0 }), [applyView]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !imageData) return;
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        canvas.getContext('2d').putImageData(imageData, 0, 0);
    }, [imageData]);

    useEffect(() => {
        const canvas = compareCanvasRef.current;
        if (!canvas || !compareImageData) return;
        canvas.width = compareImageData.width;
        canvas.height = compareImageData.height;
        canvas.getContext('2d').putImageData(compareImageData, 0, 0);
    }, [compareImageData, compare]);

    // A different page (crop, rotate, new upload) starts fitted again — keeping a
    // pan from the previous geometry would park the user off in empty space.
    const shapeKey = imageData ? `${imageData.width}x${imageData.height}` : '';
    useEffect(() => {
        resetView();
    }, [shapeKey, resetView]);

    /**
     * Zoom about a point measured from the viewport CENTRE (which is also the
     * transform origin), so the pixel under that point stays put.
     */
    const zoomAbout = useCallback((nextZoom, px, py) => {
        const current = zoomRef.current;
        const next = clampZoom(nextZoom);
        if (next === current) return;
        const ratio = next / current;
        const o = offsetRef.current;
        applyView(next, {
            x: px - (px - o.x) * ratio,
            y: py - (py - o.y) * ratio,
        });
    }, [applyView]);

    const stepZoom = useCallback((delta) => {
        zoomAbout(zoomRef.current + delta, 0, 0);
    }, [zoomAbout]);

    // Wheel has to be bound by hand: React's onWheel is passive, so it cannot
    // preventDefault, and the page would scroll while you zoom.
    //
    // `hasImage` in the deps is load-bearing: the first render has no image and
    // returns null, so the viewport ref is still empty and there is nothing to
    // bind to. Without it the listener is never attached at all.
    const hasImage = !!imageData;
    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return undefined;

        const onWheel = (e) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            zoomAbout(
                zoomRef.current * Math.exp(-e.deltaY / 320),
                e.clientX - rect.left - rect.width / 2,
                e.clientY - rect.top - rect.height / 2,
            );
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [hasImage, zoomAbout]);

    const onPointerDown = useCallback((e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointersRef.current.size === 2) {
            const [a, b] = [...pointersRef.current.values()];
            pinchRef.current = {
                distance: Math.hypot(b.x - a.x, b.y - a.y) || 1,
                zoom: zoomRef.current,
            };
            panRef.current = null;
            return;
        }

        if (zoomRef.current > 1) {
            panRef.current = { x: e.clientX, y: e.clientY, offset: offsetRef.current };
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        const pinch = pinchRef.current;
        if (pinch && pointersRef.current.size === 2) {
            const [a, b] = [...pointersRef.current.values()];
            const distance = Math.hypot(b.x - a.x, b.y - a.y) || 1;
            applyView(pinch.zoom * (distance / pinch.distance), offsetRef.current);
            return;
        }

        const pan = panRef.current;
        if (!pan) return;
        applyView(zoomRef.current, {
            x: pan.offset.x + (e.clientX - pan.x),
            y: pan.offset.y + (e.clientY - pan.y),
        });
    }, [applyView]);

    const endPointer = useCallback((e) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) pinchRef.current = null;
        if (pointersRef.current.size === 0) panRef.current = null;
    }, []);

    /**
     * Compare is a WIPE, not a swap.
     *
     * Toggling between two pictures makes you hold one in your head to judge the
     * other; a split with a handle you drag puts cleaned and original against the
     * same line, on the same words.
     *
     * The line is measured against the VIEWPORT, not the page. Tie it to the page
     * and zooming to 800% drags the divider off toward one edge — it is pinned to
     * a spot on the paper that has since scrolled away, and the user is left with
     * a wipe they cannot reach. Screen-anchored, the divider is a fixed window
     * onto the original: zoom and pan move the paper behind it, the line stays put.
     */
    const [split, setSplit] = useState(50);
    const splitDragRef = useRef(false);

    const splitFromEvent = useCallback((clientX) => {
        const port = viewportRef.current;
        if (!port) return null;
        const rect = port.getBoundingClientRect();
        if (!rect.width) return null;
        return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    }, []);

    const onSplitDown = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        splitDragRef.current = true;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);

    const onSplitMove = useCallback((e) => {
        if (!splitDragRef.current) return;
        e.stopPropagation();
        const next = splitFromEvent(e.clientX);
        if (next != null) setSplit(next);
    }, [splitFromEvent]);

    const onSplitUp = useCallback((e) => {
        splitDragRef.current = false;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);

    // A fresh comparison starts down the middle rather than wherever the last
    // page was left, which would look like the tool had done nothing.
    useEffect(() => { if (compare) setSplit(50); }, [compare]);

    /**
     * Where the page sits on screen, in viewport coordinates.
     *
     * The chrome (divider, handle, badges) is positioned from this rather than
     * riding inside the zoom transform, so it keeps its own size at any zoom.
     * Re-measured whenever the view changes and whenever the panel resizes.
     */
    const [frameBox, setFrameBox] = useState(null);
    const [viewportBox, setViewportBox] = useState(null);
    useEffect(() => {
        const measure = () => {
            const frame = frameRef.current;
            const port = viewportRef.current;
            if (!frame || !port) return;
            const f = frame.getBoundingClientRect();
            const p = port.getBoundingClientRect();
            setViewportBox({ width: p.width, height: p.height });
            setFrameBox({
                left: f.left - p.left,
                top: f.top - p.top,
                width: f.width,
                height: f.height,
            });
        };
        measure();
        const port = viewportRef.current;
        if (!port || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(port);
        return () => observer.disconnect();
    }, [view, compare, imageData]);

    if (!imageData) return null;

    const { zoom, offset } = view;
    const zoomed = zoom > 1;
    // Both halves must be the same page, or the wipe would compare two geometries
    // and every line would jump across the divider.
    const showSplit = compare
        && !!compareImageData
        && compareImageData.width === imageData.width
        && compareImageData.height === imageData.height;

    // The divider's screen x, and the same line expressed in the page's own
    // coordinates — which is what clip-path needs, since the compare canvas sits
    // inside the zoom transform. Recomputing this from the measured box is what
    // keeps the boundary still on screen while the page moves under it.
    // The page's size on screen at fit, worked out here rather than left to CSS.
    // `max-h-full max-w-full` on a canvas only keeps its proportions when the
    // parent has a definite height; when it has not, one axis is capped and the
    // other is not, and the page is squashed. Both layers take this exact size,
    // so cleaned and original are the same rectangle at any zoom.
    // Never larger than the working image: it is a preview, not an upscale.
    const fitScale = viewportBox && viewportBox.width && viewportBox.height
        ? Math.min(1, viewportBox.width / imageData.width, viewportBox.height / imageData.height)
        : null;
    const fitStyle = fitScale
        ? { width: Math.round(imageData.width * fitScale), height: Math.round(imageData.height * fitScale) }
        : null;

    const splitX = ((viewportBox?.width ?? 0) * split) / 100;
    const clipPct = frameBox && frameBox.width
        ? Math.max(0, Math.min(100, ((splitX - frameBox.left) / frameBox.width) * 100))
        : split;

    return (
        <div className="group relative flex h-full w-full items-center justify-center overflow-hidden">
            <div
                ref={viewportRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endPointer}
                onPointerCancel={endPointer}
                onDoubleClick={() => (zoomed ? resetView() : zoomAbout(2, 0, 0))}
                className={`flex h-full w-full items-center justify-center overflow-hidden touch-none ${
                    zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
                }`}
            >
                <div
                    ref={frameRef}
                    style={{
                        ...(fitStyle || {}),
                        // Until the viewport has been measured, fall back to the CSS fit.
                        ...(fitStyle ? {} : { maxWidth: '100%', maxHeight: '100%' }),
                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                        transformOrigin: 'center center',
                    }}
                    className="relative flex-none"
                >
                    <canvas
                        ref={canvasRef}
                        draggable={false}
                        className="block h-full w-full rounded-lg bg-white shadow-[0_2px_10px_rgba(28,24,21,0.12)]"
                    />

                    {showSplit ? (
                        <>
                            {/* The original sits ON TOP, clipped to the right of the
                                handle — so what you drag away is the cleanup. This
                                one DOES belong inside the transform: it is the page,
                                and it has to zoom and pan with the page under it. */}
                            <canvas
                                ref={compareCanvasRef}
                                draggable={false}
                                style={{ clipPath: `inset(0 0 0 ${clipPct}%)` }}
                                className="pointer-events-none absolute inset-0 h-full w-full rounded-lg"
                            />
                        </>
                    ) : null}
                </div>
            </div>

            {/* Divider, handle and badges live OUTSIDE the zoom transform and are
                placed in VIEWPORT coordinates. Inside it they were scaled by the
                zoom (a fist-sized handle at 276%) and dragged around by the pan.
                Chrome holds still: same size, same place, whatever the page does. */}
            {showSplit ? (
                <>
                    {/* Brand colour, not white: the cleaned side of a scan IS white
                        paper, and a white line on it is no line at all. */}
                    <div
                        className="pointer-events-none absolute inset-y-0 z-10 w-[2px] bg-primary shadow-[0_0_0_1px_rgba(255,255,255,0.65)]"
                        style={{ left: splitX }}
                    />
                    <span
                        className="pointer-events-none absolute left-2 top-2 z-10 rounded-full bg-[#1C1815]/70 px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity"
                        style={{ opacity: split > 12 ? 1 : 0 }}
                    >
                        {labels.cleaned || t('documentCleanupImage.showCleaned')}
                    </span>
                    <span
                        className="pointer-events-none absolute right-2 top-2 z-10 rounded-full bg-[#1C1815]/70 px-2.5 py-1 text-[11px] font-semibold text-white transition-opacity"
                        style={{ opacity: split < 88 ? 1 : 0 }}
                    >
                        {labels.original || t('documentCleanupImage.originalBadge')}
                    </span>
                    <button
                        type="button"
                        onPointerDown={onSplitDown}
                        onPointerMove={onSplitMove}
                        onPointerUp={onSplitUp}
                        onPointerCancel={onSplitUp}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowLeft') setSplit((v) => Math.max(0, v - 2));
                            if (e.key === 'ArrowRight') setSplit((v) => Math.min(100, v + 2));
                        }}
                        aria-label={labels.compare || t('documentCleanupImage.compare')}
                        title={labels.compare || t('documentCleanupImage.compare')}
                        className="absolute top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-[0_2px_10px_rgba(28,24,21,0.35)] touch-none"
                        style={{ left: splitX }}
                    >
                        <IconArrowsHorizontal size={17} stroke={2.2} />
                    </button>
                </>
            ) : null}

            <div className="absolute right-2 top-2 flex items-center gap-1 rounded-xl border border-[#ECE8E4] bg-white/95 p-1 shadow-sm dark:border-zinc-700 dark:bg-zinc-900/95">
                <button
                    type="button"
                    onClick={() => stepZoom(-STEP)}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label={t('documentCleanupImage.zoomOut')}
                    title={t('documentCleanupImage.zoomOut')}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#3D3833] transition-colors hover:bg-[#F7F5F3] disabled:opacity-40 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    <IconMinus size={15} stroke={2.2} />
                </button>
                <span className="min-w-[44px] text-center text-[12px] font-semibold tabular-nums text-[#3D3833] dark:text-zinc-200">
                    {Math.round(zoom * 100)}%
                </span>
                <button
                    type="button"
                    onClick={() => stepZoom(STEP)}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label={t('documentCleanupImage.zoomIn')}
                    title={t('documentCleanupImage.zoomIn')}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#3D3833] transition-colors hover:bg-[#F7F5F3] disabled:opacity-40 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    <IconPlus size={15} stroke={2.2} />
                </button>
                <button
                    type="button"
                    onClick={resetView}
                    disabled={!zoomed}
                    aria-label={t('documentCleanupImage.fitToScreen')}
                    title={t('documentCleanupImage.fitToScreen')}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#3D3833] transition-colors hover:bg-[#F7F5F3] disabled:opacity-40 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                    <IconMaximize size={15} stroke={1.9} />
                </button>
            </div>

            {actions.length ? (
                <div className="absolute inset-x-0 bottom-0 z-10 flex flex-wrap items-center justify-center gap-2 bg-gradient-to-t from-[#1C1815]/75 to-transparent px-2 pb-3 pt-10 transition-opacity md:pointer-events-none md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:opacity-100">
                    {actions.map(({ id, icon: Icon, label: actionLabel, onClick, active, disabled }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={onClick}
                            disabled={disabled}
                            aria-label={actionLabel}
                            title={actionLabel}
                            aria-pressed={active || undefined}
                            className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                active
                                    ? 'bg-primary text-white hover:bg-primary/90'
                                    : 'bg-white/95 text-[#3D3833] hover:bg-white dark:bg-zinc-900/95 dark:text-zinc-200'
                            }`}
                        >
                            <Icon size={20} stroke={1.8} />
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
